import React, { useState, useEffect } from 'react';
import { AutoBillingConfig, EmailConfig, AutoBillingService } from '../services/autoBillingService';
import { Client, Company } from '../types';
import { AutoInvoicePreview } from './AutoInvoicePreview';
import { 
  CalendarIcon, 
  GlobeAltIcon, 
  CurrencyEuroIcon, 
  EnvelopeIcon,
  CogIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface AutoBillingManagerProps {
  clients: Client[];
  currentCompany?: Company | null;
  onInvoiceGenerated: (invoice: any) => void;
  nextInvoiceNumber: number;
}

export const AutoBillingManager: React.FC<AutoBillingManagerProps> = ({
  clients,
  currentCompany,
  onInvoiceGenerated,
  nextInvoiceNumber
}) => {
  const [configs, setConfigs] = useState<AutoBillingConfig[]>([]);
  const [emailConfig, setEmailConfig] = useState<EmailConfig | null>(null);
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [showNewConfig, setShowNewConfig] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<AutoBillingConfig | null>(null);
  const [newConfig, setNewConfig] = useState<Partial<AutoBillingConfig>>({
    billingDay: 1,
    monthlyAmount: 50,
    ivaPercentage: 21,
    irpfPercentage: 15,
    isActive: true,
    description: 'Mantenimiento web mensual'
  });

  useEffect(() => {
    loadConfigs();
    loadEmailConfig();
  }, []);

  const loadConfigs = () => {
    const configs = AutoBillingService.getAutoBillingConfigs();
    setConfigs(configs);
  };

  const loadEmailConfig = () => {
    const config = AutoBillingService.getEmailConfig();
    setEmailConfig(config);
  };

  const saveConfig = () => {
    if (!newConfig.clientId || !newConfig.domain || !newConfig.monthlyAmount) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    const client = clients.find(c => c.id === newConfig.clientId);
    if (!client) return;

    const config: AutoBillingConfig = {
      id: Date.now().toString(),
      clientId: newConfig.clientId!,
      clientName: client.name,
      domain: newConfig.domain!,
      monthlyAmount: newConfig.monthlyAmount!,
      billingDay: newConfig.billingDay!,
      isActive: newConfig.isActive!,
      nextBillingDate: calculateNextBillingDate(newConfig.billingDay!),
      description: newConfig.description!,
      ivaPercentage: newConfig.ivaPercentage!,
      irpfPercentage: newConfig.irpfPercentage!,
      companyId: currentCompany?.id || ''
    };

    AutoBillingService.saveAutoBillingConfig(config);
    setConfigs([...configs, config]);
    setShowNewConfig(false);
    setNewConfig({
      billingDay: 1,
      monthlyAmount: 50,
      ivaPercentage: 21,
      irpfPercentage: 15,
      isActive: true,
      description: 'Mantenimiento web mensual'
    });
  };

  const calculateNextBillingDate = (day: number): string => {
    const today = new Date();
    let nextDate = new Date(today.getFullYear(), today.getMonth(), day);
    
    if (nextDate <= today) {
      nextDate = new Date(today.getFullYear(), today.getMonth() + 1, day);
    }
    
    return nextDate.toISOString();
  };

  const showPreview = (config: AutoBillingConfig) => {
    setPreviewConfig(config);
  };

  const toggleConfig = (configId: string) => {
    const updatedConfigs = configs.map(config => 
      config.id === configId ? { ...config, isActive: !config.isActive } : config
    );
    setConfigs(updatedConfigs);
    updatedConfigs.forEach(config => AutoBillingService.saveAutoBillingConfig(config));
  };

  const deleteConfig = (configId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta configuración?')) {
      AutoBillingService.deleteAutoBillingConfig(configId);
      setConfigs(configs.filter(c => c.id !== configId));
    }
  };

  const saveEmailConfig = () => {
    if (emailConfig) {
      AutoBillingService.saveEmailConfig(emailConfig);
      setShowEmailConfig(false);
    }
  };

  const processBilling = async () => {
    const dueConfigs = AutoBillingService.checkDailyBilling();
    
    if (dueConfigs.length === 0) {
      toast.error('No hay facturas pendientes de generar');
      return;
    }

    let currentInvoiceNumber = nextInvoiceNumber;
    
    for (const config of dueConfigs) {
      try {
        // Generar factura
        const invoice = AutoBillingService.generateMonthlyInvoice(config, currentInvoiceNumber);
        
        // Enviar por email
        const client = clients.find(c => c.id === config.clientId);
        if (client && emailConfig) {
          const sent = await AutoBillingService.sendInvoiceByEmail(invoice, client, emailConfig);
          if (sent) {
            console.log(`✅ Factura ${invoice.number} enviada a ${client.name}`);
          }
        }
        
        // Actualizar fecha de próxima facturación
        AutoBillingService.updateNextBillingDate(config.id);
        
        // Notificar al dashboard
        onInvoiceGenerated(invoice);
        
        currentInvoiceNumber++;
        
      } catch (error) {
        console.error(`❌ Error procesando facturación para ${config.clientName}:`, error);
      }
    }
    
    loadConfigs();
    toast.error(`Procesadas ${dueConfigs.length} facturas`);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <CogIcon className="h-8 w-8 mr-3 text-blue-600" />
          Facturación Automática Mensual
        </h2>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowEmailConfig(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
          >
            <EnvelopeIcon className="h-5 w-5 mr-2" />
            Configurar Email
          </button>
          <button
            onClick={() => setShowNewConfig(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nueva Configuración
          </button>
          <button
            onClick={processBilling}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
          >
            <PlayIcon className="h-5 w-5 mr-2" />
            Procesar Facturación
          </button>
        </div>
      </div>

      {/* Configuraciones existentes */}
      <div className="space-y-4 mb-6">
        <h3 className="text-lg font-semibold text-gray-700">Configuraciones Activas</h3>
        {configs.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No hay configuraciones de facturación automática</p>
        ) : (
          configs.map(config => (
            <div key={config.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {config.isActive ? (
                        <PlayIcon className="h-5 w-5 text-green-600" />
                      ) : (
                        <PauseIcon className="h-5 w-5 text-gray-400" />
                      )}
                      <span className="font-medium text-gray-900">{config.clientName}</span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span className="flex items-center">
                        <GlobeAltIcon className="h-4 w-4 mr-1" />
                        {config.domain}
                      </span>
                      <span className="flex items-center">
                        <CurrencyEuroIcon className="h-4 w-4 mr-1" />
                        {config.monthlyAmount}€/mes
                      </span>
                      <span className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        Día {config.billingDay}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    <span>Próxima facturación: {new Date(config.nextBillingDate).toLocaleDateString('es-ES')}</span>
                    {config.lastBilledDate && (
                      <span className="ml-4">Última: {new Date(config.lastBilledDate).toLocaleDateString('es-ES')}</span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => showPreview(config)}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm hover:bg-blue-200"
                    title="Vista previa de la factura"
                  >
                    👁️ Vista Previa
                  </button>
                  <button
                    onClick={() => toggleConfig(config.id)}
                    className={`px-3 py-1 rounded text-sm ${
                      config.isActive 
                        ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                    }`}
                  >
                    {config.isActive ? 'Pausar' : 'Activar'}
                  </button>
                  <button
                    onClick={() => deleteConfig(config.id)}
                    className="bg-red-100 text-red-800 px-3 py-1 rounded text-sm hover:bg-red-200"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal Nueva Configuración */}
      {showNewConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Nueva Configuración de Facturación</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                <select
                  value={newConfig.clientId || ''}
                  onChange={(e) => setNewConfig({...newConfig, clientId: e.target.value})}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Seleccionar cliente</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dominio</label>
                <input
                  type="text"
                  value={newConfig.domain || ''}
                  onChange={(e) => setNewConfig({...newConfig, domain: e.target.value})}
                  placeholder="ejemplo.com"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Importe Mensual (€)</label>
                <input
                  type="number"
                  value={newConfig.monthlyAmount || ''}
                  onChange={(e) => setNewConfig({...newConfig, monthlyAmount: parseFloat(e.target.value)})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Día de Facturación</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={newConfig.billingDay || ''}
                  onChange={(e) => setNewConfig({...newConfig, billingDay: parseInt(e.target.value)})}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IVA %</label>
                  <input
                    type="number"
                    value={newConfig.ivaPercentage || ''}
                    onChange={(e) => setNewConfig({...newConfig, ivaPercentage: parseInt(e.target.value)})}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IRPF %</label>
                  <input
                    type="number"
                    value={newConfig.irpfPercentage || ''}
                    onChange={(e) => setNewConfig({...newConfig, irpfPercentage: parseInt(e.target.value)})}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={saveConfig}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                Guardar
              </button>
              <button
                onClick={() => setShowNewConfig(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Configuración Email */}
      {showEmailConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Configuración de Email</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Gmail</label>
                <input
                  type="email"
                  value={emailConfig?.smtpUser || ''}
                  onChange={(e) => setEmailConfig({...emailConfig!, smtpUser: e.target.value})}
                  placeholder="tuemail@gmail.com"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña de Aplicación</label>
                <input
                  type="password"
                  value={emailConfig?.smtpPassword || ''}
                  onChange={(e) => setEmailConfig({...emailConfig!, smtpPassword: e.target.value})}
                  placeholder="Contraseña de aplicación Gmail"
                  className="w-full border rounded-lg px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usa una contraseña de aplicación de Gmail, no tu contraseña principal
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Empresa</label>
                <input
                  type="text"
                  value={emailConfig?.fromName || ''}
                  onChange={(e) => setEmailConfig({...emailConfig!, fromName: e.target.value})}
                  placeholder="Daniel Vázquez Facturación"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={saveEmailConfig}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
              >
                Guardar
              </button>
              <button
                onClick={() => setShowEmailConfig(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Vista Previa */}
      {previewConfig && (
        <AutoInvoicePreview
          config={previewConfig}
          client={clients.find(c => c.id === previewConfig.clientId)!}
          onClose={() => setPreviewConfig(null)}
        />
      )}
    </div>
  );
}; 